/**
 * @module components/NetworksTable
 * @author Charles Blais <charles.blais@canada.ca>
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ControlLabel, FormGroup, Table } from 'react-bootstrap';
import { useTable, useFilters, useSortBy } from 'react-table';
import queryString from 'query-string';

import { getStatusIndicator, getStatusColor } from '../../libs/display';
import { useLanguageState } from '../../contexts/LanguageProvider';
import { useAppState } from '../../contexts/AppStateProvider';

import { Networks } from '../../libs/networks';
import {
  DefaultColumnFilter,
  SelectColumnFilter,
  SelectMultipleColumnFilter,
  filterMultipleIncludes
} from '../../libs/react-table-filters';



const NetworksReactTable = ({columns, data}) => {

  const [ lang ] = useLanguageState();

  // Column hook
  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }), []);

  // Table hook
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
    columns,
    data,
    defaultColumn,
    initialState: {
      sortBy: [
          {
              id: 'station',
              desc: false
          }
      ]
    }
  }, useFilters, useSortBy);

  // Generate the cell property
  const getCellProps = (cell) => {
    if(cell.column.Header === lang.status) {
      return {
        style: {
          backgroundColor: getStatusColor(cell.row.original)
        }
      }
    }
    return { style: {} };
  };

  return (
    <Table {...getTableProps()} bordered hover>
      <thead>
        {headerGroups.map(headerGroup => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column, idx) => {
              const sort = (
                <span>
                  {column.isSorted
                    ? column.isSortedDesc
                      ? ' 🔽'
                      : ' 🔼'
                    : ''}
                </span>
              );
              return (
                <th {
                  ...column.getHeaderProps(
                    (!column.Filter.name.includes('Select'))
                    ?column.getSortByToggleProps()
                    :{}
                  )}
                >
                  {column.canFilter ? (
                    <FormGroup
                      controlId={`id${idx}`}
                    >
                      <ControlLabel>{column.render('Header')}{sort}</ControlLabel>
                      {column.render('Filter')}
                    </FormGroup>
                  ) : <>{column.render('Header')}{sort}</>}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row);
          return (
            <tr {...row.getRowProps()}>
              {row.cells.map((cell, j) => (
                <td {...cell.getCellProps([{
                    className: cell.column.className,
                    style: cell.column.style,
                  }, getCellProps(cell)])}>
                  {cell.render('Cell')}
                </td>
              ))}
            </tr>
          )
        })}
      </tbody>
    </Table>
  );
};


const NetworksTable = ({networks}) => {
  const [ lang ] = useLanguageState();
  const [ state ] = useAppState();

  // Create column information
  const columns = useMemo(() => [{
    Header: lang.details,
    accessor: (originalRow) => (
      <a
        href={`?${queryString.stringify({
          network: originalRow.networkCode,
          station: originalRow.code,
          starttime: originalRow.starttime.format(state.datetimeFormat),
          ...(originalRow.endtime
              ? { endtime: originalRow.endtime.format(state.datetimeFormat) }
              : {}),
        })}`}
        aria-label={lang.moreDetails}
      >
        <span className="glyphicon glyphicon-info-sign" aria-hidden="true"></span> <span className='hidden'>{lang.details}</span>
      </a>
    ),
    disableFilters: true,
  }, {
    Header: lang.network,
    accessor: 'networkCode',
    disableFilters: (networks.length <= 1),
    Filter: SelectMultipleColumnFilter,
    filter: filterMultipleIncludes
  }, {
    Header: lang.station,
    id: 'station',
    accessor: 'code',
  }, {
    Header: lang.name,
    accessor: 'name',
  }, {
    Header: lang.status,
    accessor: (originalRow) => {
      switch(getStatusIndicator(originalRow)){
        case -1: return lang.close;
        case 0: return lang.open;
        case 1: return lang.planned;
        default: return '';
      }
    },
    Filter: SelectColumnFilter,
  }, {
    Header: lang.startDate,
    accessor: (originalRow) => 
      (originalRow.starttime) ? originalRow.starttime.format(state.datetimeFormat) : '',
  }, {
    Header: lang.endDate,
    accessor: (originalRow) =>
      (originalRow.endtime) ? originalRow.endtime.format(state.datetimeFormat) : '',
  }], [
    state.datetimeFormat,
    lang,
    networks,
  ]);

  const data = useMemo(() => networks.toData(), [networks]);

  return (
    <NetworksReactTable
      columns={columns}
      data={data}
    />
  );
};


NetworksTable.propTypes = {
  /**
   * Networks object as described in libs/networks.js
   */
  networks: PropTypes.instanceOf(Networks),
};


NetworksTable.defaultProps = {
  networks: [],
};


export default NetworksTable;
